import { BrowserRouter as Router, Link, useNavigate } from 'react-router-dom';
import { Suspense, useState, useEffect } from 'react';
import { DarkModeToggle } from './components/dark-mode-toggle';
import { FeedbackButton } from './components/feedback-button';
import { AppSidebar } from './components/app-sidebar';
import { SyfoAdminIcon } from './components/icons/syfo-admin-icon';
import { SyfoText } from './components/icons/syfo-text';
import { SyfoSidebarTrigger } from './components/syfo-sidebar-trigger';
import { AdminRoutes } from './routes';
import { AppErrorBoundary } from './components/error-boundaries';
import { SidebarInset, SidebarProvider } from './components/ui/sidebar';
import { useAppDispatch, useAppSelector } from './stores/hooks';
import { checkAuthThunk } from './stores/thunks/check-auth-thunk';
import './app.css';

function App() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Authentication state from Redux
  const { isAuthenticated } = useAppSelector(state => state.auth);
  
  // Loading state for auth check
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  
  const handleNavigateHome = () => {
    navigate('/');
  };

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await dispatch(checkAuthThunk()).unwrap();
      } catch (error) {
        console.error('[App] Auth check failed:', error);
      } finally {
        setIsLoadingAuth(false);
      }
    };
    
    checkAuth();
  }, [dispatch]);

  // Show loading spinner during initial auth check
  if (isLoadingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Hide header and sidebar when not authenticated (showing login page)
  const showHeader = isAuthenticated;
  const showSidebar = isAuthenticated;

  return (
    <AppErrorBoundary onNavigateHome={handleNavigateHome}>
      <SidebarProvider>
        {/* Main Content Area */}
        <SidebarInset>
          {/* Global App Header - only show when authenticated */}
          {showHeader && (
            <header className="sticky top-0 z-50 bg-background flex items-center justify-between py-2 px-4 border-b">
              <div className="flex items-center gap-2">
                <Link className="flex items-center" to="/">
                  <SyfoAdminIcon className="h-10 w-10 pr-1" />
                  <SyfoText className="h-10 w-auto" />
                </Link>
              </div>
              <div className="flex items-center gap-2">
                <FeedbackButton />
                <DarkModeToggle />
                <SyfoSidebarTrigger />
              </div>
            </header>
          )}

          {/* Page Content */}
          <div className="flex-1 overflow-auto">
            <Suspense fallback={
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading...</p>
                </div>
              </div>
            }>
              <AdminRoutes />
            </Suspense>
          </div>
        </SidebarInset>
        
        {/* Sidebar - only show when authenticated */}
        {showSidebar && <AppSidebar />}
      </SidebarProvider>
    </AppErrorBoundary>
  );
}

// Wrapper component to provide Router context
function AppWithRouter() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWithRouter;